import type { ClubPlanType } from "@teamz/common";
import type { ComponentProps } from "react";

import CupIcon from "@/assets/cup.svg";
import EyeIcon from "@/assets/eye.svg";
import FavIcon from "@/assets/heart.svg";
import MsgIcon from "@/assets/msg-none.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";
import type { Routes } from "@/routes";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMe } from "@/context/user-context";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import { useOpenMessageThread } from "@/hooks/use-open-message-thread";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { routes } from "@/routes";

const EmptyButton = () => {
  return (
    <div className="flex h-14 w-full items-center justify-center rounded-md bg-neutral-50">
      <DotsIcon />
    </div>
  );
};

const OfferButton = ({ offer, variant, ...props }: { offer: RouterOutputs["club"]["getClub"]["matchOffers"][number]; variant: "blue" | "purple" } & ComponentProps<"button">) => {
  const { t } = useTranslation(["profile"]);
  return (
    <button
      type="button"
      className={cn(
        "pointer-events-none flex h-14 w-full items-center gap-1 rounded-lg p-1",
        variant === "blue" && "bg-blue-200",
        variant === "purple" && "bg-purple-200",
      )}
      {...props}
    >
      <div className="relative flex h-full flex-1 items-center justify-center rounded-md bg-white">
        <span className="text-sm font-bold text-blue-500">
          {
            (offer.playerRankingType === "FFT" && offer.playerRanking === "T100")
              ? `N${offer.playerRankingNumber}`
              : offer.playerRanking
          }
        </span>

        <div
          className={cn(
            "absolute left-0 top-0 rounded-br px-1.5 text-xs font-medium leading-[14px]",
            variant === "blue" && "bg-blue-200 text-blue-500",
            variant === "purple" && "bg-purple-200 text-purple-500",
          )}
        >
          {offer.playerRankingType}
        </div>
        <div
          className={cn(
            "absolute bottom-0 right-0 rounded-tl px-1.5 text-xs font-medium leading-[14px]",
            variant === "blue" && "bg-blue-200 text-blue-500",
            variant === "purple" && "bg-purple-200 text-purple-500",
          )}
        >
          {t("profile:or-more")}
        </div>
      </div>
    </button>
  );
};

export function ClubListItem({ club, fromPath, onClick, className }: { club: RouterOutputs["club"]["listClubs"][number]; fromPath: Routes["clubs"] | Routes["favorites"] | Routes["admin"]; onClick?: () => void; className?: string }) {
  const { t } = useTranslation(["common", "profile"]);
  const navigate = useNavigate();
  const breakpoint = useBreakpoints();
  const { user } = useMe();
  const { getCountryName } = useTranslationUtils();

  const menOffers = club.matchOffers.filter(matchOffer => matchOffer.playerGender === "male");
  const womenOffers = club.matchOffers.filter(matchOffer => matchOffer.playerGender === "female");
  const openMessageThread = useOpenMessageThread();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: player } = useQuery(trpc.player.getPlayer.queryOptions({ playerId: user.id }));

  const { mutate } = useMutation(trpc.player.updateFavoritesClubs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      queryClient.invalidateQueries(trpc.player.getPlayerFavoriteClubs.queryFilter());
    },
  }));

  const isClubAlreadyFav = player?.favoritesClubs.includes(club.id);

  const updateFavorite = () => {
    mutate({
      clubId: club.id,
    });
  };

  function upsertThread() {
    openMessageThread(club.clubRepresentativeId);
  }

  function handleRedirect() {
    if (fromPath === routes.admin) {
      navigate(routes.getAdminClubProfilePath(club.clubRepresentativeId), { state: { fromPath } });
    }
    else {
      navigate(routes.getClubPath(club.clubRepresentativeId), { state: { fromPath } });
    }
  }

  function getPlanLabel(plan: ClubPlanType) {
    switch (plan) {
      case "pack_equipe":
        return "P.E";
      case "pack_tournoi":
        return "P.T";
      case "pack_equipe_tournoi":
        return "P.E.T";
      default:
        return "No plan";
    }
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        "grid cursor-default grid-cols-1 gap-2 rounded-[18px] border border-neutral-400 bg-white p-2.5 md:grid-cols-2 md:flex-row md:rounded-none md:border-x-0 md:border-t-0 md:first:rounded-t-[18px] md:last:rounded-b-[18px] md:last:border-b-0 lg:grid-cols-[1fr_auto_auto_1fr_auto] lg:gap-3",
        className,
        menOffers.length === 0 && womenOffers.length === 0 && "bg-neutral-50",
      )}
    >
      <div
        className="order-1 flex cursor-pointer items-center gap-2 lg:max-w-[295px]"
        onClick={(fromPath === routes.clubs || fromPath === routes.favorites) ? () => navigate(routes.getClubPath(club.clubRepresentativeId), { state: { fromPath } }) : undefined}
      >

        <div className="relative shrink-0">
          <img
            src={club.imgUrl ?? undefined}
            alt={`Profil de ${club.clubName}`}
            className="pointer-events-none size-14 select-none rounded-xl object-cover"
          />
          {user.isAdmin && club.plan && (
            <span className="absolute inset-x-0 top-0 rounded-t-xl bg-orange-500 text-center text-xs font-bold text-orange-100">
              {getPlanLabel(club.plan)}
            </span>
          )}
        </div>

        <div className="flex w-full min-w-0 flex-col gap-1">
          <h1 className="truncate text-lg font-semibold">{club.clubName.toUpperCase()}</h1>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full bg-neutral-100 p-0.5 lg:hidden">
              <CircleFlag countryCode={club.clubCountryCode.toLowerCase()} className="size-4 rounded-full" />
              <span className="block truncate pr-3 text-sm font-medium">
                {`${club.clubCity}, ${getCountryName(club.clubCountryCode)}`}
              </span>
            </div>
            <span className="hidden truncate text-sm font-medium text-neutral-500 lg:block">
              {`${club.clubCity}, ${getCountryName(club.clubCountryCode)}`}
            </span>
          </div>
        </div>
      </div>

      <div className="order-2 hidden items-center justify-center lg:flex">
        <div className="flex h-fit w-auto max-w-48 shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 p-0.5">
          <CircleFlag countryCode={club.clubCountryCode.toLowerCase()} className="size-4 rounded-full" />
          <span className="pr-2 text-sm font-medium">
            {club.clubCountryCode.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="order-3 flex gap-1 px-2 md:order-4 md:flex-col lg:order-3 lg:w-[160px] lg:px-0">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Badge variant="blue" size="compact" className="shrink-0">{t("common:man").charAt(0)}</Badge>
          <div className="min-w-0 flex-1">
            <div className="truncate">
              {club.clubTeamsMenDivision.length
                ? club.clubTeamsMenDivision.map((division, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={index} className="text-sm font-medium">
                      {division}
                      {index < club.clubTeamsMenDivision.length - 1 ? " / " : ""}
                    </span>
                  ))
                : "-"}
            </div>
          </div>
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Badge variant="purple" size="compact" className="shrink-0">{t("common:woman").charAt(0)}</Badge>
          <div className="min-w-0 flex-1">
            <div className="truncate">
              {club.clubTeamsWomenDivision.length
                ? club.clubTeamsWomenDivision.map((division, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={index} className="text-sm font-medium">
                      {division}
                      {index < club.clubTeamsWomenDivision.length - 1 ? " / " : ""}
                    </span>
                  ))
                : "-"}
            </div>
          </div>
        </div>
      </div>

      <div className="order-4 flex w-full flex-col gap-1.5 rounded-xl border border-neutral-400 p-1.5 md:order-3 md:justify-center md:border-0 md:p-0 lg:order-4">
        <div className="flex items-center justify-between px-2 py-1.5 md:hidden">
          <div className="flex items-center gap-1.5">
            <CupIcon />
            <span className="text-sm font-medium">{t("profile:match")}</span>
          </div>
          <span className="text-sm font-medium text-orange-500">{t("profile:players-wanted")}</span>
        </div>

        <div className="flex items-center gap-1">
          <div className="flex flex-1 items-center gap-1">
            {
              [0, 1].map((index) => {
                const offer = menOffers[index];
                return offer
                  ? (
                      <OfferButton key={offer.id} offer={offer} variant="blue" />
                    )
                  : (
                      <EmptyButton key={index + 10} />
                    );
              })
            }
          </div>
          <div className="flex flex-1 items-center gap-1">
            {
              [0, 1].map((index) => {
                const offer = womenOffers[index];
                return offer
                  ? (
                      <OfferButton key={offer.id} offer={offer} variant="purple" />
                    )
                  : (
                      <EmptyButton key={index} />
                    );
              })
            }
          </div>
        </div>
      </div>

      {(fromPath === routes.clubs || fromPath === routes.favorites) && (
        <div className="order-5 flex items-center gap-1">
          <Button
            variant="outline"
            size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
            onClick={updateFavorite}
            className="active:border-neutral-300"
          >
            <FavIcon
              className={
                isClubAlreadyFav
                  ? "fill-[#FE5000] "
                  : "fill-white "
              }
            />
            <span className="hidden sm:inline-flex md:hidden">{t("common:mobile-nav.favorites")}</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              upsertThread();
            }}
            size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
          >
            <MsgIcon />
            <span className="hidden sm:inline-flex md:hidden">Message</span>
          </Button>
          <Button
            className="w-full lg:w-fit"
            size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
            onClick={handleRedirect}
          >
            <EyeIcon />
            <span className="lg:hidden">{t("common:go-to-club-profile")}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
